import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Plus, Trash2, FileDown, Upload, FileText, ScrollText, Check, ListPlus, Send } from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES, fmtMoney } from "../../lib/currency";

const EMPTY_ITEM = { item_name: "", sku: "", details: "", custom_details: "", dimensions: "", quantity: 1, unit_price: 0 };

const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";
const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";

function downloadPo(poId, fallback) {
  return api.get(`/purchase-orders/${poId}/download`, { responseType: "blob" }).then((res) => {
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : fallback;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  });
}

function POForm({ projectId, project, initialItems, initialVendor, onCreated, onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10), po_number: "",
    company_name: user?.business_name || "", company_address: user?.company_address || "",
    company_phone: user?.company_phone || "", company_email: user?.company_email || user?.email || "",
    vendor_name: initialVendor || "", vendor_contact: "", trade_account: "",
    project_name: project?.name || "", client_name: project?.client_name || project?.client_email || "",
    client_reference: "", lead_time: "", ship_to: "", special_instructions: "", payment_terms: "",
    shipping: 0, taxes: 0, currency: "CAD",
  });
  const [items, setItems] = useState(
    initialItems && initialItems.length ? initialItems.map((it) => ({ ...EMPTY_ITEM, ...it })) : [{ ...EMPTY_ITEM }]
  );
  const [grandTotal, setGrandTotal] = useState("");
  const [gtEdited, setGtEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((p) => [...p, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0),
    [items]
  );
  const computedTotal = subtotal + (Number(f.shipping) || 0) + (Number(f.taxes) || 0);
  const displayedTotal = gtEdited ? grandTotal : computedTotal;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        shipping: Number(f.shipping) || 0,
        taxes: Number(f.taxes) || 0,
        grand_total: gtEdited ? (Number(grandTotal) || 0) : computedTotal,
        items: items
          .filter((it) => it.item_name || it.sku || it.details || Number(it.unit_price))
          .map((it) => ({ ...it, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 })),
      };
      const { data } = await api.post(`/projects/${projectId}/purchase-orders`, payload);
      toast.success("Purchase order created");
      onCreated(data);
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create PO"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5 mt-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="po-form">
      <div className="grid grid-cols-3 gap-4">
        <div><Label className={lbl}>PO number</Label>
          <Input data-testid="po-number-input" value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Date</Label>
          <Input data-testid="po-date-input" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Currency</Label>
          <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger data-testid="po-currency" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B] mb-3">Your Company</p>
        <Label className={lbl}>Company name</Label>
        <Input data-testid="po-company-name" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} className={fieldCls} />
        <Label className={`${lbl} block mt-3`}>Address</Label>
        <Input data-testid="po-company-address" value={f.company_address} onChange={(e) => set("company_address", e.target.value)} className={fieldCls} />
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div><Label className={lbl}>Phone</Label>
            <Input value={f.company_phone} onChange={(e) => set("company_phone", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Email</Label>
            <Input value={f.company_email} onChange={(e) => set("company_email", e.target.value)} className={fieldCls} /></div>
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B] mb-3">Vendor</p>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={lbl}>Vendor name</Label>
            <Input data-testid="po-vendor-name" value={f.vendor_name} onChange={(e) => set("vendor_name", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Contact name</Label>
            <Input value={f.vendor_contact} onChange={(e) => set("vendor_contact", e.target.value)} className={fieldCls} /></div>
        </div>
        <Label className={`${lbl} block mt-3`}>Trade account number</Label>
        <Input value={f.trade_account} onChange={(e) => set("trade_account", e.target.value)} className={fieldCls} />
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B] mb-3">Project</p>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={lbl}>Project name</Label>
            <Input value={f.project_name} onChange={(e) => set("project_name", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Client name</Label>
            <Input value={f.client_name} onChange={(e) => set("client_name", e.target.value)} className={fieldCls} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div><Label className={lbl}>Client reference #</Label>
            <Input value={f.client_reference} onChange={(e) => set("client_reference", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Lead time</Label>
            <Input value={f.lead_time} onChange={(e) => set("lead_time", e.target.value)} placeholder="e.g. 6–8 weeks" className={fieldCls} /></div>
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B]">Line Items</p>
          <button type="button" data-testid="po-add-item" onClick={addItem}
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#A68A6B] hover:text-[#8C7154]">
            <Plus size={13} /> Add item
          </button>
        </div>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-[#EBE5E0] p-3 space-y-2" data-testid={`po-item-${i}`}>
              <div className="flex items-center gap-2">
                <Input placeholder="Item name" data-testid={`po-item-name-${i}`} value={it.item_name}
                  onChange={(e) => setItem(i, "item_name", e.target.value)} className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <Input placeholder="SKU" value={it.sku} onChange={(e) => setItem(i, "sku", e.target.value)}
                  className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] w-32" />
                {items.length > 1 && (
                  <button type="button" data-testid={`po-remove-item-${i}`} onClick={() => removeItem(i)} className="text-[#5C5651] hover:text-[#9E4747] shrink-0"><Trash2 size={15} /></button>
                )}
              </div>
              <Input placeholder="Details" value={it.details} onChange={(e) => setItem(i, "details", e.target.value)}
                className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Custom details" value={it.custom_details} onChange={(e) => setItem(i, "custom_details", e.target.value)}
                  className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <Input placeholder="Dimensions" value={it.dimensions} onChange={(e) => setItem(i, "dimensions", e.target.value)}
                  className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              </div>
              <div className="grid grid-cols-3 gap-2 items-end">
                <div><Label className={lbl}>Qty</Label>
                  <Input type="number" min="0" step="any" data-testid={`po-item-qty-${i}`} value={it.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)} className={fieldCls} /></div>
                <div><Label className={lbl}>Unit price</Label>
                  <Input type="number" min="0" step="0.01" data-testid={`po-item-price-${i}`} value={it.unit_price}
                    onChange={(e) => setItem(i, "unit_price", e.target.value)} className={fieldCls} /></div>
                <div className="text-right">
                  <p className={lbl}>Amount</p>
                  <p className="font-medium text-[#2B2622] mt-2">{fmtMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0), f.currency)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4 space-y-2 max-w-xs ml-auto">
        <div className="flex justify-between text-sm text-[#5C5651]"><span>Subtotal</span><span data-testid="po-subtotal">{fmtMoney(subtotal, f.currency)}</span></div>
        <div className="flex justify-between items-center gap-2 text-sm">
          <span className="text-[#5C5651]">Shipping</span>
          <Input type="number" step="0.01" data-testid="po-shipping" value={f.shipping} onChange={(e) => set("shipping", e.target.value)}
            className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] h-8 w-28 text-right" /></div>
        <div className="flex justify-between items-center gap-2 text-sm">
          <span className="text-[#5C5651]">Taxes</span>
          <Input type="number" step="0.01" data-testid="po-taxes" value={f.taxes} onChange={(e) => set("taxes", e.target.value)}
            className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B] h-8 w-28 text-right" /></div>
        <div className="flex justify-between items-center gap-2 pt-2 border-t border-[#EBE5E0]">
          <span className="text-[#2B2622] font-medium uppercase tracking-wider text-xs">Grand Total</span>
          <Input type="number" step="0.01" data-testid="po-grand-total" value={displayedTotal}
            onChange={(e) => { setGtEdited(true); setGrandTotal(e.target.value); }}
            className="rounded-none border-[#A68A6B] focus-visible:ring-0 h-9 w-32 text-right font-medium" /></div>
        {gtEdited && (
          <button type="button" data-testid="po-reset-total" onClick={() => { setGtEdited(false); setGrandTotal(""); }}
            className="text-[10px] uppercase tracking-wider text-[#A68A6B] hover:underline block ml-auto">Auto-calculate</button>
        )}
      </div>

      <div className="border-t border-[#EBE5E0] pt-4 space-y-3">
        <div><Label className={lbl}>Ship to address</Label>
          <Textarea data-testid="po-ship-to" value={f.ship_to} onChange={(e) => set("ship_to", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Payment terms</Label>
          <Input value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. Net 30" className={fieldCls} /></div>
        <div><Label className={lbl}>Special instructions</Label>
          <Textarea value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} className={fieldCls} /></div>
      </div>

      <Button data-testid="po-submit" type="submit" disabled={busy}
        className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
        {busy ? "Creating…" : "Create Purchase Order"}
      </Button>
    </form>
  );
}

export default function PurchaseOrdersTab({ projectId, project }) {
  const [pos, setPos] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [ffeOpen, setFfeOpen] = useState(false);
  const [ffeItems, setFfeItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [seed, setSeed] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/projects/${projectId}/purchase-orders`).then((r) => setPos(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const openFfe = async () => {
    setFfeOpen(true);
    try {
      const r = await api.get(`/projects/${projectId}/ffe`);
      setFfeItems(r.data);
      setSelected(Object.fromEntries(r.data.map((i) => [i.id, true])));
    } catch { setFfeItems([]); }
  };

  const continueFromFfe = () => {
    const chosen = ffeItems.filter((i) => selected[i.id]);
    if (!chosen.length) return;
    const items = chosen.map((i) => ({
      item_name: i.name || "", sku: "",
      details: [i.category, i.room].filter(Boolean).join(" · "),
      custom_details: i.notes || "", dimensions: "",
      quantity: i.quantity || 1, unit_price: i.unit_price || 0,
    }));
    const vendors = [...new Set(chosen.map((i) => i.vendor).filter(Boolean))];
    setSeed({ items, vendor: vendors.length === 1 ? vendors[0] : "" });
    setFormKey((k) => k + 1);
    setFfeOpen(false);
    setCreateOpen(true);
  };

  const newBlankPo = () => { setSeed(null); setFormKey((k) => k + 1); };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      const { data } = await api.post(`/projects/${projectId}/purchase-orders/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPos((p) => [data, ...p]);
      setFile(null); setLabel(""); setUploadOpen(false);
      toast.success("Purchase order uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/purchase-orders/${id}`); setPos((p) => p.filter((x) => x.id !== id)); };

  const setPaid = async (po, paid, paid_date) => {
    try {
      const { data } = await api.put(`/purchase-orders/${po.id}/paid`, { paid, paid_date: paid_date || "" });
      setPos((p) => p.map((x) => (x.id === po.id ? data : x)));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const togglePaid = (po) => {
    if (po.paid) setPaid(po, false);
    else setPaid(po, true, new Date().toISOString().slice(0, 10));
  };

  const handleDownload = async (po) => {
    setDownloading(po.id);
    try { await downloadPo(po.id, `PO-${po.po_number || "order"}.pdf`); }
    catch { toast.error("Could not download PDF"); }
    finally { setDownloading(null); }
  };

  const openEmail = (po) => {
    setEmailFor(po);
    const vc = po.vendor_contact || "";
    setEmailTo(vc.includes("@") ? vc : "");
    setEmailMsg("");
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/purchase-orders/${emailFor.id}/email`, { to: emailTo, message: emailMsg });
      toast.success(`Sent to ${data.to}`);
      setEmailFor(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="po-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Procurement</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Purchase Orders</h3>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={ffeOpen} onOpenChange={setFfeOpen}>
            <DialogTrigger asChild>
              <Button data-testid="po-from-ffe-btn" onClick={openFfe}
                className="rounded-none bg-transparent border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-5 px-5">
                <ListPlus size={15} className="mr-2" /> From FF&amp;E
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">Create PO from FF&amp;E</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5651]">Select FF&amp;E items to pre-load as line items.</DialogDescription>
              </DialogHeader>
              {ffeItems.length === 0 ? (
                <p className="text-sm text-[#5C5651] py-6 text-center">No FF&amp;E items on this project yet.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-[#A68A6B] py-2 border-b border-[#EBE5E0]">
                    <span>{Object.values(selected).filter(Boolean).length} selected</span>
                    <button type="button" data-testid="po-ffe-select-all"
                      onClick={() => setSelected(Object.fromEntries(ffeItems.map((i) => [i.id, true])))}
                      className="hover:text-[#8C7154]">Select all</button>
                  </div>
                  <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#F2EFEB]">
                    {ffeItems.map((i) => (
                      <label key={i.id} data-testid={`po-ffe-option-${i.id}`}
                        className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-[#F9F8F6]">
                        <Checkbox checked={!!selected[i.id]}
                          onCheckedChange={(v) => setSelected((p) => ({ ...p, [i.id]: !!v }))}
                          className="rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B] data-[state=checked]:border-[#A68A6B]" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-[#2B2622] truncate">{i.name}</span>
                          <span className="block text-[11px] text-[#9b938b] truncate">
                            {[i.vendor, i.category, i.room].filter(Boolean).join(" · ") || "—"}
                          </span>
                        </span>
                        <span className="text-[11px] text-[#5C5651] shrink-0 text-right">
                          {i.quantity} × {fmtMoney(i.unit_price)}
                        </span>
                      </label>
                    ))}
                  </div>
                  <Button data-testid="po-ffe-continue" onClick={continueFromFfe}
                    disabled={Object.values(selected).filter(Boolean).length === 0}
                    className="w-full mt-2 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                    Continue to PO
                  </Button>
                </>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-po-btn"
                className="rounded-none bg-transparent border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-5 px-5">
                <Upload size={15} className="mr-2" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">Upload purchase order</DialogTitle>
                <DialogDescription className="sr-only">Upload a scan or document of an existing purchase order</DialogDescription>
              </DialogHeader>
              <form onSubmit={upload} className="space-y-4 mt-2">
                <div>
                  <Label className={lbl}>Label (optional)</Label>
                  <Input data-testid="po-upload-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. RH Order #4821" className={fieldCls} />
                </div>
                <div>
                  <Label className={lbl}>File (PDF or image)</Label>
                  <input data-testid="po-upload-input" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files[0])}
                    className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                  {file && <p className="text-[11px] text-[#9b938b] mt-1 truncate">{file.name}</p>}
                </div>
                <Button data-testid="po-upload-submit" type="submit" disabled={busy || !file}
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                  {busy ? "Uploading…" : "Upload"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-po-btn" onClick={newBlankPo}
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> New PO
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">New purchase order</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5651]">Your studio branding is added to the PDF automatically. All fields are optional.</DialogDescription>
              </DialogHeader>
              <POForm key={formKey} projectId={projectId} project={project}
                initialItems={seed?.items} initialVendor={seed?.vendor}
                onCreated={(po) => setPos((p) => [po, ...p])} onClose={() => setCreateOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {pos.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <ScrollText size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          No purchase orders yet. Create one or upload an existing PO.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
          {pos.map((po) => (
            <div key={po.id} className="bg-white p-5 flex flex-col" data-testid={`po-card-${po.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={18} strokeWidth={1.5} className="text-[#A68A6B] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-[#2B2622] font-medium truncate">{po.po_number || "Purchase Order"}</p>
                    <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">
                      {po.kind === "uploaded" ? "Uploaded" : "Created"}{po.vendor_name ? ` · ${po.vendor_name}` : ""}
                    </p>
                  </div>
                </div>
                <button data-testid={`delete-po-${po.id}`} onClick={() => remove(po.id)} className="text-[#5C5651] hover:text-[#9E4747] shrink-0"><Trash2 size={15} strokeWidth={1.5} /></button>
              </div>
              <div className="text-[11px] text-[#9b938b] mt-2 space-y-0.5">
                {po.date && <p>Date: {po.date}</p>}
                {po.kind === "created" && <p>Total: {fmtMoney(po.grand_total, po.currency)}</p>}
              </div>

              <div className="mt-3 flex items-center gap-2" data-testid={`po-paid-row-${po.id}`}>
                <button type="button" data-testid={`po-paid-toggle-${po.id}`} onClick={() => togglePaid(po)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors ${po.paid ? "bg-[#506B52] text-white" : "border border-[#EBE5E0] text-[#5C5651] hover:border-[#506B52] hover:text-[#506B52]"}`}>
                  <Check size={13} strokeWidth={2} /> {po.paid ? "Paid" : "Mark paid"}
                </button>
                {po.paid && (
                  <input type="date" data-testid={`po-paid-date-${po.id}`} value={po.paid_date || ""}
                    onChange={(e) => setPaid(po, true, e.target.value)}
                    className="rounded-none border border-[#EBE5E0] focus:outline-none focus:border-[#506B52] text-[11px] text-[#2B2622] px-2 py-1" />
                )}
              </div>

              {po.sent_to && <p className="text-[11px] text-[#9b938b] mt-2">Sent to {po.sent_to}</p>}

              <div className="mt-4 pt-3 border-t border-[#F2EFEB] flex flex-wrap items-center gap-2">
                <button data-testid={`download-po-${po.id}`} onClick={() => handleDownload(po)} disabled={downloading === po.id}
                  className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider disabled:opacity-60">
                  <FileDown size={14} strokeWidth={1.5} />
                  {downloading === po.id ? "Preparing…" : "PDF"}
                </button>
                <button data-testid={`email-po-${po.id}`} onClick={() => openEmail(po)}
                  className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                  <Send size={13} strokeWidth={1.5} /> Send to vendor
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!emailFor} onOpenChange={(o) => { if (!o) setEmailFor(null); }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="email-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Send purchase order</DialogTitle>
            <DialogDescription className="text-sm text-[#5C5651]">The PO PDF will be emailed to the vendor as an attachment.</DialogDescription>
          </DialogHeader>
          <form onSubmit={sendEmail} className="space-y-4 mt-2">
            <div>
              <Label className={lbl}>Vendor email</Label>
              <Input data-testid="email-po-to" type="email" required value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="vendor@email.com" className={fieldCls} />
            </div>
            <div>
              <Label className={lbl}>Message (optional)</Label>
              <Textarea data-testid="email-po-message" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className={fieldCls} />
            </div>
            <Button type="submit" disabled={sending} data-testid="email-po-submit"
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {sending ? "Sending…" : "Send Purchase Order"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
